import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Power } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import {
  createAgreementVersion,
  fetchAgreementVersions,
  setAgreementActive,
} from "@/lib/super-admin";
import { SERVICE_LABEL, SERVICE_TYPES, type ServiceType } from "@/lib/services";

export const Route = createFileRoute("/_authenticated/super-admin/agreements")({
  head: () => ({
    meta: [
      { title: "Agreements · Landoo Super Admin" },
      { name: "description", content: "Publish and version partner agreement documents." },
    ],
  }),
  component: AgreementsPage,
});

const input =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-xs outline-none focus:border-brand";

function AgreementsPage() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["super-admin", "agreements"],
    queryFn: fetchAgreementVersions,
  });
  const [form, setForm] = useState({
    service_type: "sale" as ServiceType,
    version: "",
    title: "",
    body: "",
    active: true,
  });

  const submit = async () => {
    if (!form.version.trim() || !form.title.trim() || !form.body.trim()) {
      toast.error("Version, title and body are required");
      return;
    }
    try {
      await createAgreementVersion(form);
      toast.success("Agreement version published");
      setForm({ ...form, version: "", title: "", body: "" });
      qc.invalidateQueries({ queryKey: ["super-admin", "agreements"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not publish");
    }
  };

  const toggle = async (id: string, active: boolean) => {
    try {
      await setAgreementActive(id, active);
      toast.success(active ? "Version activated" : "Version deactivated");
      qc.invalidateQueries({ queryKey: ["super-admin", "agreements"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update");
    }
  };

  return (
    <DashboardShell title="Agreements" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <section className="mb-4 rounded-2xl border border-border bg-card p-4 shadow-soft">
        <h2 className="text-sm font-bold text-brown-dark">Publish new agreement version</h2>
        <p className="text-xs text-muted-foreground">
          Existing signed agreements are never modified.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <label className="block">
            <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
              Service
            </div>
            <select
              className={input}
              value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value as ServiceType })}
            >
              {SERVICE_TYPES.map((s) => (
                <option key={s} value={s}>
                  {SERVICE_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
              Version
            </div>
            <input
              className={input}
              placeholder="e.g. v1.2"
              value={form.version}
              onChange={(e) => setForm({ ...form, version: e.target.value })}
            />
          </label>
          <label className="block sm:col-span-2">
            <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
              Title
            </div>
            <input
              className={input}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label className="block sm:col-span-2">
            <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
              Body
            </div>
            <textarea
              className={`${input} min-h-32`}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-brown-dark">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Make this the active version for {SERVICE_LABEL[form.service_type]}
          </label>
        </div>
        <button
          onClick={submit}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-xs font-semibold text-white shadow-soft"
        >
          <Plus className="h-4 w-4" /> Publish version
        </button>
      </section>

      <h2 className="mb-2 text-sm font-bold text-brown-dark">Published versions</h2>
      {isLoading ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading…
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No agreement versions published yet.
        </div>
      ) : (
        <div className="space-y-2">
          {data.map((v) => (
            <div key={v.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 sm:flex sm:flex-wrap sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase text-brown-dark">
                      {SERVICE_LABEL[v.service_type as ServiceType] ?? v.service_type}
                    </span>
                    <span className="rounded-full bg-brand/30 px-2 py-0.5 text-[10px] font-bold text-brown-dark">
                      {v.version}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        v.active ? "bg-rent/15 text-rent" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {v.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <h3 className="mt-1 truncate text-sm font-semibold text-brown-dark">{v.title}</h3>
                  <p className="text-[11px] text-muted-foreground">
                    Effective {new Date(v.effective_at).toLocaleDateString()}
                  </p>
                  <p className="mt-2 line-clamp-3 whitespace-pre-line text-xs text-brown-dark">
                    {v.body}
                  </p>
                </div>
                <button
                  onClick={() => toggle(v.id, !v.active)}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${
                    v.active ? "border border-coral/40 text-coral" : "bg-rent text-white"
                  }`}
                >
                  <Power className="h-3.5 w-3.5" />
                  {v.active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
